import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { db } from '../config/firebase';
import { collection, query, onSnapshot, orderBy, limit } from 'firebase/firestore';

function Overview() {
  const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    partners: 0,
    activeTasks: 0,
    employees: 0,
    totalQueries: 0
  });

  const [recentQueries, setRecentQueries] = useState([]);

  useEffect(() => {
    // Fetch Partners Count
    const unsubPartners = onSnapshot(collection(db, 'partners'), (snapshot) => {
      setStats(prev => ({ ...prev, partners: snapshot.size }));
    });

    // Fetch Employees Count
    const unsubEmployees = onSnapshot(collection(db, 'employees'), (snapshot) => {
      setStats(prev => ({ ...prev, employees: snapshot.size }));
    });

    // Fetch Active Tasks Count (assuming 'tasks' collection and status field)
    // We'll fetch all and filter to avoid potential missing index issues for now
    const unsubTasks = onSnapshot(collection(db, 'tasks'), (snapshot) => {
      const active = snapshot.docs.filter(doc => {
        const status = doc.data().status?.toLowerCase();
        return status === 'pending' || status === 'in-progress';
      }).length;
      setStats(prev => ({ ...prev, activeTasks: active }));
    });

    // Fetch Recent Queries
    const qQueries = query(collection(db, 'queries'), orderBy('createdAt', 'desc'), limit(5));
    const unsubQueries = onSnapshot(qQueries, (snapshot) => {
      const queries = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecentQueries(queries);
      // Also update total queries count if needed, or fetch separately.
      // Since we need total count, let's just listen to the whole collection for size if we strictly need total count, 
      // but "Active Tasks" card replaced "Total Tasks"? 
      // The original UI had "Active Tasks". Let's stick to that.
      // But we have "Recent Activity" showing queries.
    });

    return () => {
      unsubPartners();
      unsubEmployees();
      unsubTasks();
      unsubQueries();
    };
  }, []);

  // Helper to format timestamps
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    // Handle Firestore Timestamp or Date string
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = (now - date) / 1000; // seconds

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'text-green-600';
      case 'in-progress': return 'text-blue-600';
      case 'pending': return 'text-yellow-600';
      case 'rejected': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div
        className="flex-1 bg-gray-50 overflow-y-auto transition-all duration-300"
        style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}
      >
        <div className="p-6 min-h-screen">
          {/* Header */}
          <div className="mb-8 flex items-center">
            {isMobile && (
              <button
                onClick={toggleMobileSidebar}
                className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            <div>
              <h1 className="text-3xl font-bold text-brand-dark">Admin Overview</h1>
              <p className="text-gray-600 mt-2">Welcome back! Here's what's happening in your dashboard today.</p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Partners Card */}
            <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Partners</p>
                  <h3 className="text-2xl font-bold text-brand-dark mt-1">{stats.partners}</h3>
                  <p className="text-sm text-green-600 mt-1 flex items-center font-medium">
                    <span className="mr-1">●</span> Real-time
                  </p>
                </div>
                <div className="bg-brand-golden/10 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-brand-golden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Tasks Card */}
            <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Tasks</p>
                  <h3 className="text-2xl font-bold text-blue-600 mt-1">{stats.activeTasks}</h3>
                  <p className="text-sm text-gray-500 mt-1">Pending or In Progress</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Employees Card */}
            <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Employees</p>
                  <h3 className="text-2xl font-bold text-green-600 mt-1">{stats.employees}</h3>
                  <p className="text-sm text-green-600 mt-1 flex items-center font-medium">
                    <span className="mr-1">●</span> Real-time
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Revenue Card Removed */}
          </div>

          {/* Quick Actions Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-3"> {/* Expanded to full width since System Status is hidden */}
              <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-semibold text-brand-dark mb-4">Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> {/* Changed to 2 columns */}
                  <button
                    onClick={() => navigate('/admin/partners', { state: { openCreateModal: true } })}
                    className="flex items-center justify-center p-4 bg-brand-golden/10 rounded-lg hover:bg-brand-golden/20 transition-colors group"
                  >
                    <div className="text-center">
                      <div className="w-12 h-12 bg-brand-golden rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                        <svg className="w-6 h-6 text-brand-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </div>
                      <p className="font-medium text-brand-dark">Add Partner</p>
                    </div>
                  </button>

                  {/* Create Task (Query) Removed */}

                  <button
                    onClick={() => navigate('/admin/employees', { state: { openCreateModal: true } })}
                    className="flex items-center justify-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors group"
                  >
                    <div className="text-center">
                      <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                      </div>
                      <p className="font-medium text-brand-dark">Add Employee</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* System Status Commented Out */}
            {/* 
            <div>
              <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-semibold text-brand-dark mb-4">System Status</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Server Status</span>
                    <span className="flex items-center text-green-600 font-medium">
                      <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                      Online
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Database</span>
                    <span className="flex items-center text-green-600 font-medium">
                      <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                      Connected
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">API Status</span>
                    <span className="flex items-center text-green-600 font-medium">
                      <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                      Active
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Last Backup</span>
                    <span className="text-gray-600">2 hours ago</span>
                  </div>
                </div>
              </div>
            </div>
            */}
          </div>

          {/* Recent Queries Activity */}
          <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-semibold text-brand-dark mb-4">Recent Queries</h2>
            <div className="space-y-4">
              {recentQueries.length > 0 ? (
                recentQueries.map((query) => (
                  <div key={query.id} className="flex items-center space-x-4 p-4 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer" onClick={() => navigate('/admin/queries')}>
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-brand-dark">{query.title || 'Untitled Query'}</p>
                      <p className="text-sm text-gray-600">{query.serviceType} • {query.address}</p>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getStatusColor(query.status)} bg-opacity-10`}>
                        {query.status?.toUpperCase()}
                      </span>
                      <p className="text-xs text-gray-400 mt-1">{formatTime(query.createdAt)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No recent queries found.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Overview;